import { merge } from 'ts-deepmerge';
import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { MaybeLoanID } from 'src/common/types';
import { findLatestPurchasePrice } from 'src/experiences/MortgageDashboard/GetServerSideProps/utils/findLatestPurchasePrice';
import { ServerSideModuleData } from 'src/experiences/MortgageDashboard/GetServerSideProps/utils/getServerSideModuleData';
import { useGetUpsellModuleDataQuery, ZHLPersonalizedNextStepState } from 'src/services/graphql/generated/graphql';
import { isUUID } from 'src/utils/validators/isUUID';
import { NO_LEAD_AVAILABLE } from 'src/analytics/constants';
import { useFirstPartyEligibility } from 'src/experiences/MortgageDashboard/hooks/useFirstPartyEligibility/useFirstPartyEligibility';
import { toUpsellCTAVariant, UpsellCTAVariant } from '../anticorruption/UpsellCTAVariant';

const isServerSideDataSufficient = (data: Pick<ServerSideModuleData, 'Upsell'>) => {
    const { homeLoansDashboardByLoanId } = data.Upsell;
    const { personalizedNextSteps, currentlySelectedLoan } = homeLoansDashboardByLoanId ?? {};
    const { currentState: status, loanApplicationExperienceURL } = personalizedNextSteps ?? {};
    return !!currentlySelectedLoan && !!status && !!loanApplicationExperienceURL;
};

export type UpsellComputedState = {
    error: ApolloError | undefined;
    isLoading: boolean;
    currentState: ZHLPersonalizedNextStepState | undefined;
    variant: UpsellCTAVariant | null;
    isFirstPartyExperience: boolean;
    lead_id: string;
    firstPartyPreApprovalDetailsURL: string | null;
    experienceURL: string | null;
    borrower_id: string | null;
    documentURL: string | null;
    downloadURL: string | null;
    street: string | null;
    valuation: number | null;
    purchasePrice: string | null;
    /**
     * Returned purely for testing purposes
     */
    _isLoanIDInvalid: boolean;
    /**
     * Returned purely for testing purposes
     */
    _shouldSkipUpsellModuleDataQuery: boolean;
};

export const useUpsellComputedState = ({
    loanID,
    serverFetchedData,
}: {
    loanID: MaybeLoanID;
    serverFetchedData: Pick<ServerSideModuleData, 'Upsell'>;
}): UpsellComputedState => {
    const { Upsell } = serverFetchedData;
    const isLoanIDInvalid = !isUUID(loanID);
    const shouldSkipUpsellModuleDataQuery = isLoanIDInvalid || isServerSideDataSufficient(serverFetchedData);

    const { loading, data, error } = useGetUpsellModuleDataQuery({
        variables: {
            id: loanID as string,
        },
        skip: shouldSkipUpsellModuleDataQuery,
    });

    const mergedData = merge(data ?? {}, Upsell);
    const { homeLoansDashboardByLoanId } = mergedData;
    const { personalizedNextSteps, currentlySelectedLoan } = homeLoansDashboardByLoanId ?? {};
    const { lead, attributes, preApprovalLetterURL, currentlyLoggedInBorrower } = currentlySelectedLoan ?? {};
    const { id: borrower_id = null } = currentlyLoggedInBorrower ?? {};
    const { id: lead_id = NO_LEAD_AVAILABLE } = lead ?? {};
    const { currentState, loanApplicationExperienceURL } = personalizedNextSteps ?? {};
    const { valuation = null, purchaseLocation } = attributes ?? {};
    const { ctaPreApprovalUrlWithDomain } = currentlySelectedLoan?.quickAppFeatures ?? {};
    const { documentURL = null, downloadURL = null } = preApprovalLetterURL ?? {};
    const { street = null } = purchaseLocation ?? {};
    const { preApprovalLetters = null } = currentlySelectedLoan ?? {};
    const { currentExperience } = personalizedNextSteps ?? {};
    const purchasePrice = findLatestPurchasePrice({ preApprovalLetters });

    const { isFirstPartyExperience } = useFirstPartyEligibility(currentState, currentExperience);

    const firstPartyPreApprovalDetailsURL = ctaPreApprovalUrlWithDomain ?? null;
    const experienceURL = useMemo(() => {
        if (!loanApplicationExperienceURL) {
            return null;
        }
        return loanApplicationExperienceURL;
    }, [loanApplicationExperienceURL]);
    const isLoading = useMemo(() => {
        const hasServerSideData = isServerSideDataSufficient(serverFetchedData);
        if (hasServerSideData) {
            return false;
        }
        return isLoanIDInvalid || loading || !currentState;
    }, [serverFetchedData, isLoanIDInvalid, loading, currentState]);

    return {
        error,
        isLoading,
        currentState,
        variant: toUpsellCTAVariant(currentState, isFirstPartyExperience),
        isFirstPartyExperience,
        lead_id,
        firstPartyPreApprovalDetailsURL,
        experienceURL,
        borrower_id,
        documentURL,
        downloadURL,
        street,
        valuation,
        purchasePrice,
        // Returned purely for testing purposes
        _isLoanIDInvalid: isLoanIDInvalid,
        // Returned purely for testing purposes
        _shouldSkipUpsellModuleDataQuery: shouldSkipUpsellModuleDataQuery,
    };
};
